import React, { useEffect, useMemo, useState, useRef } from 'react'
import { ClassId, DetailedMatchResult, GameState, HeroId } from '../types'
import { fetchHeros, OkMainHero } from '../services/okMainApi'
import { getAsset } from '../services/assets'
import tavernBg from '../assets/ui/fantasy/tavern_bg.png'
import woodPanel from '../assets/ui/fantasy/wood_panel.png'
import parchmentTexture from '../assets/ui/fantasy/parchment_texture.png'
import btnWood from '../assets/ui/fantasy/btn_wood.png'
// classic frame for avatar as requested
import classicFrame from '../assets/avatars/classic/frame.png'

import lvl1 from '../assets/ui/fantasy/1lvl.png'
import lvl2 from '../assets/ui/fantasy/2lvl.png'
import lvl3 from '../assets/ui/fantasy/3lvl.png'
import lvl4 from '../assets/ui/fantasy/4lvl.png'
import lvl5 from '../assets/ui/fantasy/5lvl.png'
import lvl6 from '../assets/ui/fantasy/6lvl.png'
import lvl7 from '../assets/ui/fantasy/7lvl.png'
import lvl8 from '../assets/ui/fantasy/8lvl.png'
import lvl9 from '../assets/ui/fantasy/9lvl.png'
import lvl10 from '../assets/ui/fantasy/10lvl.png'
import lvl11 from '../assets/ui/fantasy/11lvl.png'

import { getHeroDescriptionByName } from '../data/heroData'
import { getApiBase } from '../services/apiBase'
import { isTouchDevice } from '../services/device'
import { Language, t } from '../data/translations'
import { useTouchHint } from '../hooks/useTouchHint'
import { aggregatePlayerStats, getBestClass, getWorstClass } from '../services/statsAggregator'
import { AchievementTrack } from './profile/AchievementTrack'
import { EMOTES } from './emotes/EmoteBubble'
import { ACHIEVEMENT_REWARDS } from '../data/rewards'
import { useParticles } from '../contexts/ParticleContext'
import AdminPanel from './AdminPanel'
import { useMobileAutoScrollHint } from '../hooks/useMobileAutoScrollHint'

const LEVEL_AVATARS: Record<number, string> = {
  1: lvl1, 2: lvl2, 3: lvl3, 4: lvl4, 5: lvl5, 6: lvl6,
  7: lvl7, 8: lvl8, 9: lvl9, 10: lvl10, 11: lvl11
}

type ProfileScreenProps = {
  gameState?: GameState | null
  onBack: () => void
  availableHeroes?: string[]
  language: Language
  isModal?: boolean
  onNavigate?: (mode: any) => void
  viewOnly?: boolean
  playerData?: {
    name: string
    fame: number
    hero?: HeroId
    playerClass?: ClassId
  }
  localPlayerId?: number
}

const ProfileScreen: React.FC<ProfileScreenProps> = ({ gameState, onBack, availableHeroes, language, isModal = false, viewOnly = false, playerData, onNavigate, localPlayerId }) => {
  const rootRef = useRef<HTMLDivElement>(null)
  useMobileAutoScrollHint({ targetRef: rootRef, sceneKey: 'profile-screen' })
  const localUsername = useMemo(() => {
    if (viewOnly) return null
    try {
      const raw = localStorage.getItem('oko_auth')
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return typeof parsed?.username === 'string' ? parsed.username : null
    } catch {
      return null
    }
  }, [viewOnly])

  const me = useMemo(() => {
    if (viewOnly && playerData) return undefined
    if (gameState?.players) {
      if (localPlayerId) return gameState.players.find(p => p.id === localPlayerId)
      if (localUsername) return gameState.players.find(p => p.name === localUsername)
      return gameState.players[0]
    }
    return undefined
  }, [gameState, localPlayerId, viewOnly, playerData, localUsername])
  const [heros, setHeros] = useState<OkMainHero[]>([])
  useEffect(() => { fetchHeros().then(setHeros).catch(() => { }) }, [])

  // 1. Dervied Player State (Moved up for hoisting)
  const [serverFame, setServerFame] = useState<number>(0)
  const [serverUserLoaded, setServerUserLoaded] = useState<boolean>(false)
  const [serverMatchHistory, setServerMatchHistory] = useState<DetailedMatchResult[]>([])
  const [serverClaimedRewards, setServerClaimedRewards] = useState<number[]>([])

  const famePoints = useMemo(() => {
    if (playerData?.fame != null) return playerData.fame
    if (serverUserLoaded) return serverFame ?? 0
    return me?.famePoints ?? 0
  }, [playerData?.fame, serverUserLoaded, serverFame, me?.famePoints])

  const arcaneLevel = useMemo(() => {
    let lvl = Math.floor(famePoints / 100) + 1
    return lvl >= 11 ? 11 : lvl
  }, [famePoints])

  const arcaneProgress = useMemo(() => {
    return (famePoints >= 1000) ? 100 : (famePoints % 100)
  }, [famePoints])

  // 2. Collections & Persistence States
  const [emoteSlots, setEmoteSlots] = useState<{ id: string, type: 'premium' | 'animated' }[]>(() => {
    try {
      const authRaw = localStorage.getItem('oko_auth');
      if (authRaw) {
        const authData = JSON.parse(authRaw);
        if (authData.emoteSlots && Array.isArray(authData.emoteSlots) && authData.emoteSlots.length === 6) {
          return authData.emoteSlots;
        }
      }
    } catch (e) { }
    if (playerData && (playerData as any).emoteSlots) return (playerData as any).emoteSlots;
    if (me?.emoteSlots) return me.emoteSlots;
    return [
      { id: 'PIECE', type: 'premium' }, { id: 'SMIRK', type: 'premium' },
      { id: 'ANGER', type: 'premium' }, { id: 'SAD', type: 'premium' },
      { id: 'SHOCKED', type: 'premium' }, { id: 'THANKS', type: 'premium' }
    ];
  });


  // Sync emoteSlots if they change in the game state (e.g. initial load)
  useEffect(() => {
    if (me?.emoteSlots && Array.isArray(me.emoteSlots) && me.emoteSlots.length === 6) {
      setEmoteSlots(me.emoteSlots);
    }
  }, [me?.id]); // Only sync once when player is identified

  const [showGalleryIndex, setShowGalleryIndex] = useState<number | null>(null);

  const saveEmotes = async (newSlots: { id: string, type: 'premium' | 'animated' }[]) => {
    const validatedSlots = newSlots.map(slot => {
      if (slot.type === 'animated') {
        const reward = ACHIEVEMENT_REWARDS.find(r => r.emoteId === slot.id);
        if (reward && arcaneLevel < reward.level) return { id: slot.id, type: 'premium' as const };
      }
      return slot;
    });

    setEmoteSlots(validatedSlots);

    const authRaw = localStorage.getItem('oko_auth');
    if (authRaw) {
      try {
        const authData = JSON.parse(authRaw);
        authData.emoteSlots = validatedSlots;
        localStorage.setItem('oko_auth', JSON.stringify(authData));
        if (authData.token) {
          await fetch(`${getApiBase()}/api/update-emotes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: authData.token, emoteSlots: validatedSlots })
          });
        }
      } catch (e) {
        console.error('[ProfileScreen] Save emotes failed:', e);
      }
    }
  };

  const handleSelectEmote = (emoteId: string, type: 'premium' | 'animated') => {
    if (showGalleryIndex === null) return;
    const newSlots = [...emoteSlots];
    newSlots[showGalleryIndex] = { id: emoteId, type };
    saveEmotes(newSlots);
    setShowGalleryIndex(null);
  };

  const [editing, setEditing] = useState(false)
  const [nickname, setNickname] = useState<string>(() => {
    if (playerData?.name) return playerData.name
    try {
      const auth = JSON.parse(localStorage.getItem('oko_auth') || 'null')
      if (auth?.username) return auth.username
      return localStorage.getItem('nickname') || (me?.name || 'Player')
    } catch { return me?.name || 'Player' }
  })
  const [okId, setOkId] = useState<string>(() => {
    try { return localStorage.getItem('okId') || '' } catch { return '' }
  })

  // Achievement / Rewards Logic
  const [claimedRewards, setClaimedRewards] = useState<number[]>([])

  const handleClaimReward = (level: number) => {
    // Optimistic UI update
    setClaimedRewards(prev => [...prev, level])

    // API Call
    const base = getApiBase()
    const authRaw = localStorage.getItem('oko_auth')
    const token = authRaw ? JSON.parse(authRaw).token : null

    if (token) {
      fetch(`${base}/api/claim-reward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, level })
      })
        .then(r => r.json())
        .then(data => {
          if (data.ok) {
            if (data.claimedRewards) {
              setClaimedRewards(data.claimedRewards)
              // Sync to local auth cache
              try {
                const currentAuth = JSON.parse(localStorage.getItem('oko_auth') || '{}')
                currentAuth.claimedRewards = data.claimedRewards
                localStorage.setItem('oko_auth', JSON.stringify(currentAuth))
              } catch (e) { }
            }
          } else {
            // Revert on failure
            setClaimedRewards(prev => prev.filter(l => l !== level))
            console.error('Failed to claim reward:', data.error)
          }
        })
        .catch((err) => {
          setClaimedRewards(prev => prev.filter(l => l !== level))
          console.error('Error claiming reward:', err)
        })
    }
  }

  const captured = useMemo(() => {
    if (viewOnly) return []
    if (availableHeroes && availableHeroes.length > 0) {
      return availableHeroes.map(heroName => {
        const h = heros.find(x =>
          x.name.toUpperCase().replace(/[\s-]/g, '_') === heroName.toUpperCase().replace(/[\s-]/g, '_')
        )
        return h?.id || 0
      }).filter(id => id !== 0)
    }
    let list: number[] = []
    if (me?.capturedHeroes && me.capturedHeroes.length) list = me.capturedHeroes
    else {
      try {
        const byPlayer = localStorage.getItem(`capturedHeroes_p_${me?.id || 1}`)
        if (byPlayer) list = JSON.parse(byPlayer)
      } catch { }
      if (list.length === 0) {
        try { list = JSON.parse(localStorage.getItem('capturedHeroes') || '[]') } catch { }
      }
    }
    return list
  }, [me?.id, me?.capturedHeroes, availableHeroes, heros])

  const [factionId, setFactionId] = useState<number>(() => {
    if (viewOnly) return 0
    try { return parseInt(localStorage.getItem('factionId') || '0') || 0 } catch { return 0 }
  })

  const displayCaptured = useMemo(() => {
    const list = [...captured]
    heros.forEach(h => {
      const up = h.name.toUpperCase()
      if (factionId === 3 && up.includes('FERRIM')) { if (!list.includes(h.id)) list.push(h.id) }
      if (factionId === 1 && (up.includes('NORIM') || up.includes('TERRIK'))) { if (!list.includes(h.id)) list.push(h.id) }
      if (factionId === 2 && (up.includes('EVELION') || up.includes('CORIL'))) { if (!list.includes(h.id)) list.push(h.id) }
    })
    return list
  }, [captured, factionId, heros])

  useEffect(() => {
    const imgs = displayCaptured.map((id: number) => {
      let h = heros.find(x => x.id === id)
      if (!h) return null
      let name = h.name
      if (name === 'Erok-Buru') name = 'Verg'
      const customOnes = ['Ferrim', 'Norim', 'Evelion', 'Coril', 'Terrik', 'Gemata', 'Astrid', 'Verg', 'Lorian', 'Nyrix', 'Shanu', 'Verisk']
      if (customOnes.includes(name)) return new URL(`../assets/custom/${name}.png`, import.meta.url).href
      return ''
    }).filter(Boolean) as string[]
    imgs.forEach(src => { const img = new Image(); img.src = src })
  }, [displayCaptured, heros])

  useMobileAutoScrollHint({ targetRef: rootRef, sceneKey: 'profile-screen', enabled: !isModal })

  const EMOTE_FRAMES: Record<string, number> = { PIECE: 7, SMIRK: 8, ANGER: 10, SAD: 7, SHOCKED: 11, THANKS: 8 };
  const EMOTE_FPS: Record<string, number> = { ANGER: 3, PIECE: 2, SAD: 2, SMIRK: 3, SHOCKED: 2, THANKS: 2 };
  const particles = useParticles();
  const handleEmotePreview = (emoteId: string, buttonRect: DOMRect) => {
    if (!particles) return;
    particles.spawnSprite(buttonRect.left + buttonRect.width / 2, buttonRect.top + buttonRect.height / 2, {
      spriteUrl: `/assets/emotes/animated/${emoteId.toLowerCase()}.png`,
      frameWidth: 0, frameHeight: 0, totalFrames: EMOTE_FRAMES[emoteId] || 8, fps: EMOTE_FPS[emoteId] || 2, loop: false, scale: 1.2
    });
  };

  useEffect(() => {
    if (viewOnly) return
    const base = getApiBase()
    const authRaw = localStorage.getItem('oko_auth')
    const token = authRaw ? JSON.parse(authRaw).token : null
    if (token) {
      fetch(`${base}/api/me?token=${encodeURIComponent(token)}`)
        .then(r => r.json())
        .then(data => {
          if (data.ok && data.user) {
            setServerUserLoaded(true)
            setFactionId(data.user.faction === 'HARMONIZED' ? 1 : data.user.faction === 'CHAOTIC' ? 2 : data.user.faction === 'UNTOUCHED' ? 3 : 0);
            setServerFame(data.user.fame || 0);
            if (data.user.claimedRewards) setClaimedRewards(data.user.claimedRewards)
            if (data.user.emoteSlots && Array.isArray(data.user.emoteSlots)) {
              setEmoteSlots(data.user.emoteSlots)
              try {
                const authData = JSON.parse(localStorage.getItem('oko_auth') || '{}')
                authData.emoteSlots = data.user.emoteSlots
                localStorage.setItem('oko_auth', JSON.stringify(authData))
              } catch { }
            }
            if (data.user.matchHistory) {
              setServerMatchHistory(data.user.matchHistory);
              const localHistory = data.user.matchHistory.map((m: any) => ({
                id: m.id, date: m.date,
                winnerId: m.result === 'win' ? (me?.id || 1) : m.result === 'loss' ? ((me?.id || 1) === 1 ? 2 : 1) : 0,
                player1Name: me?.name || 'You', player2Name: m.opponentName
              }));
              localStorage.setItem('battleHistory', JSON.stringify(localHistory));
            }
          }
        }).catch(() => { });
    }
  }, [me?.id, me?.name]);

  useEffect(() => {
    if (!playerData?.name) return
    setNickname(playerData.name)
  }, [playerData?.name])

  const saveProfile = () => {
    try {
      localStorage.setItem('nickname', nickname)
      localStorage.setItem('okId', okId)
    } catch { }
    setEditing(false)
  }

  // Use server match history if available, fallback to local
  const battleHistory: Array<{ id: number; date: string; winnerId: number; player1Name?: string; player2Name?: string }> = useMemo(() => {
    if (viewOnly) return []
    if (serverMatchHistory.length > 0) {
      return serverMatchHistory.map(m => ({
        id: m.id,
        date: m.date,
        winnerId: m.result === 'win' ? (me?.id || 1) : m.result === 'loss' ? ((me?.id || 1) === 1 ? 2 : 1) : 0,
        player1Name: me?.name || 'You',
        player2Name: m.opponentName
      }));
    }
    try { return gameState?.battleHistory || JSON.parse(localStorage.getItem('battleHistory') || '[]') } catch { return gameState?.battleHistory || [] }
  }, [gameState?.battleHistory, serverMatchHistory, me?.id, me?.name])
  const winRate = useMemo(() => {
    const total = battleHistory.length
    const myId = me?.id || 1
    const wins = battleHistory.filter(g => g.winnerId === myId).length
    return total > 0 ? Math.round((wins / total) * 100) : 0
  }, [battleHistory, me?.id])

  const playerStats = useMemo(() => aggregatePlayerStats(serverMatchHistory), [serverMatchHistory])

  const [showHistory, setShowHistory] = useState(false)
  const [showHeroes, setShowHeroes] = useState(false)
  const [showStatistics, setShowStatistics] = useState(false)
  const [collectionTab, setCollectionTab] = useState<'heroes' | 'emotes'>('heroes')

  useEffect(() => {
    if (!viewOnly) return
    setEditing(false)
    setShowHistory(false)
    setShowHeroes(false)
    setShowStatistics(false)
  }, [viewOnly])

  // Hover tooltip state for hero descriptions in Heroes Collection
  const [hoveredHeroProfile, setHoveredHeroProfile] = useState<{ name: string; imgSrc: string } | null>(null)
  const [hintTriggerProfile, setHintTriggerProfile] = useState<'hover' | 'longpress' | null>(null)
  const profileHoverTimer = useRef<NodeJS.Timeout | null>(null)

  const handleProfileHeroMouseEnter = (name: string, imgSrc: string) => {
    if (profileHoverTimer.current) clearTimeout(profileHoverTimer.current)
    profileHoverTimer.current = setTimeout(() => {
      setHoveredHeroProfile({ name, imgSrc })
      setHintTriggerProfile('hover')
    }, 400)
  }

  const handleProfileHeroMouseLeave = () => {
    if (profileHoverTimer.current) clearTimeout(profileHoverTimer.current)
    setHoveredHeroProfile(null)
  }

  // Helper component for touch-aware buttons
  const TouchAwareHeroItem: React.FC<{
    name: string
    imgSrc: string
    heroId: number
    children: React.ReactNode
  }> = ({ name, imgSrc, children }) => {
    const touch = useTouchHint({
      onLongPress: () => {
        setHoveredHeroProfile({ name, imgSrc })
        setHintTriggerProfile('longpress')
      }
    })
    return (
      <div
        {...touch}
        onClick={(e) => {
          if (touch.interceptClick(e)) return
          // Action on click if any
        }}
        onMouseEnter={() => handleProfileHeroMouseEnter(name, imgSrc)}
        onMouseLeave={handleProfileHeroMouseLeave}
        className="group relative aspect-[3/4] rounded-lg overflow-hidden bg-slate-900 border border-slate-700 shadow-lg hover:border-indigo-500/50 hover:shadow-indigo-500/20 transition-all duration-300 cursor-pointer"
      >
        {children}
      </div>
    )
  }

  return (
    <div ref={rootRef} className={`w-full ${isModal ? 'h-full' : 'h-screen'} bg-cover bg-center text-amber-100 overflow-y-auto font-['Crimson_Text']`} style={{ backgroundImage: `url(${tavernBg})` }}>
      <div className={`${isModal ? 'min-h-full' : 'min-h-screen'} bg-black/60 backdrop-blur-sm p-8 relative`}>
        <div className="max-w-6xl mx-auto">
          {isModal ? (
            <button
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#3E2723] text-[#F5DEB3] hover:bg-[#5D4037] flex items-center justify-center font-bold text-xl border-2 border-[#DAA520] z-50"
              onClick={onBack}
            >
              ✕
            </button>
          ) : (
            <button
              className="mb-8 px-8 py-3 transition-all flex items-center gap-2 text-amber-100 hover:text-white hover:scale-105 font-['Cinzel'] font-bold tracking-widest text-lg shadow-xl"
              style={{
                borderStyle: 'solid',
                borderWidth: '10px',
                borderImageSource: `url(${btnWood})`,
                borderImageSlice: '30 fill',
                textShadow: '1px 1px 2px black'
              }}
              onClick={onBack}
            >
              ← {t('ui.backToMenu', language)}
            </button>
          )}

          {/* Header Section - Wood Panel */}
          <div className="relative rounded-xl overflow-hidden shadow-2xl mb-12 border-[6px] border-[#3E2723] outline outline-2 outline-[#DAA520]"
            style={{ backgroundImage: `url(${woodPanel})`, backgroundSize: 'cover' }}
          >
            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-black/40"></div>

            <div className="relative p-10 flex items-start gap-10">
              {/* Avatar with Classic Frame */}
              <div className="relative group flex-shrink-0">
                <div
                  className="w-56 h-56 relative flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    const now = Date.now();
                    const prev = Number(localStorage.getItem('admin_clicks_time') || 0);
                    let count = Number(localStorage.getItem('admin_clicks_count') || 0);

                    if (now - prev < 2000) {
                      count++;
                    } else {
                      count = 1;
                    }

                    localStorage.setItem('admin_clicks_count', count.toString());
                    localStorage.setItem('admin_clicks_time', now.toString());

                    if (count >= 20) {
                      const code = prompt(t('admin.enterCode', language));
                      if (code) {
                        const base = getApiBase();
                        const token = JSON.parse(localStorage.getItem('oko_auth') || '{}').token;

                        fetch(`${base}/api/admin/verify-code`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ token, code })
                        }).then(async res => {
                          const data = await res.json();
                          if (data.ok) {
                            localStorage.setItem('admin_clicks_count', '0');
                            localStorage.setItem('oko_is_admin', 'true');
                            onNavigate?.('ADMIN_DASHBOARD');
                          } else {
                            alert(t('admin.wrongCode', language));
                            localStorage.setItem('admin_clicks_count', '0');
                          }
                        }).catch(err => {
                          alert(t('error.network', language));
                          console.error('Admin verify error', err);
                        });
                      }
                    }
                  }}
                >
                  {/* Avatar Image (On top of frame) */}
                  <div className="absolute w-48 h-48 z-20" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                    <img src={LEVEL_AVATARS[arcaneLevel] || lvl1} className="w-full h-full object-contain" />
                  </div>

                  {/* Classic Frame (Behind avatar) */}
                  <div className="absolute inset-0 z-10 pointer-events-none" style={{
                    backgroundImage: `url(${classicFrame})`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center'
                  }}></div>

                  {/* Level Badge (Bottom of frame) */}
                  <div className="absolute -bottom-2 z-30 bg-[#2b1d0e] border-2 border-[#DAA520] rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                    <span className="font-['Cinzel'] font-bold text-[#FFD700] text-xl">{arcaneLevel}</span>
                  </div>
                </div>
              </div>

              {/* Player Info - Center Column */}
              <div className="flex-1 flex flex-col justify-center h-full pt-4">
                {!editing || viewOnly ? (
                  <h1 className="text-6xl font-['Cinzel'] font-black text-transparent bg-clip-text bg-gradient-to-b from-[#FFD700] via-[#FDB931] to-[#996515] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] mb-4">
                    {nickname}
                  </h1>
                ) : (
                  <input
                    className="text-5xl font-['Cinzel'] font-bold bg-black/40 border-b-2 border-[#DAA520] focus:outline-none text-[#FFD700] w-full max-w-lg px-4 py-2 mb-4 placeholder-amber-700/50"
                    value={nickname}
                    onChange={e => setNickname(e.target.value)}
                    placeholder={t('ui.nickname', language)}
                    autoFocus
                  />
                )}

                <div className="flex flex-wrap items-center gap-6 mb-8 text-amber-200/90 font-['Cinzel'] text-lg">
                  {viewOnly ? (
                    <>
                      {!!playerData?.hero && (
                        <span className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-lg border border-amber-900/50 shadow-inner">
                          <span className="text-[#DAA520]">🦸</span>
                          <span className="text-[#DAA520] opacity-70 uppercase text-sm tracking-wider mr-1">{t('ui.heroes', language)}:</span>
                          <span className="text-amber-100 font-bold">
                            {(() => {
                              const key = `hero.${playerData.hero}.name`
                              const label = t(key as any, language)
                              return label === key ? playerData.hero : label
                            })()}
                          </span>
                        </span>
                      )}
                      {!!playerData?.playerClass && (
                        <span className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-lg border border-amber-900/50 shadow-inner">
                          <span className="text-[#DAA520]">🛡️</span>
                          <span className="text-[#DAA520] opacity-70 uppercase text-sm tracking-wider mr-1">{t('ui.classes', language)}:</span>
                          <span className="text-amber-100 font-bold">
                            {(() => {
                              const key = `class.${playerData.playerClass}.name`
                              const label = t(key as any, language)
                              return label === key ? playerData.playerClass : label
                            })()}
                          </span>
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-lg border border-amber-900/50 shadow-inner">
                        <span className={`w-3 h-3 rounded-full shadow-[0_0_8px] ${factionId === 1 ? 'bg-green-500 shadow-green-500' : factionId === 2 ? 'bg-red-500 shadow-red-500' : factionId === 3 ? 'bg-blue-500 shadow-blue-500' : 'bg-slate-500'}`}></span>
                        <span className="text-[#DAA520] opacity-70 uppercase text-sm tracking-wider mr-1">{t('ui.faction', language)}:</span>
                        <span className="text-amber-100 font-bold">{factionId === 1 ? t('ui.faction.harmonized', language) : factionId === 2 ? t('ui.faction.chaotic', language) : factionId === 3 ? t('ui.faction.untouched', language) : t('ui.faction.none', language)}</span>
                      </span>

                      <span className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-lg border border-amber-900/50 shadow-inner">
                        <span className="text-[#DAA520]">🏆</span>
                        <span className="text-[#DAA520] opacity-70 uppercase text-sm tracking-wider mr-1">{t('ui.famePoints', language)}:</span>
                        <span className="text-amber-100 font-bold">{famePoints}</span>
                      </span>
                    </>
                  )}
                </div>

                {/* Arcane Fame Bar */}
                <div className="w-full max-w-xl">
                  <div className="flex justify-between items-end mb-1 px-1">
                    <span className="text-cyan-400 font-['Cinzel'] font-bold text-sm tracking-[0.2em] uppercase glow-text">{t('ui.arcanePower', language)}</span>
                    <span className="text-cyan-100 font-mono text-xs opacity-80">{arcaneProgress} / 100</span>
                  </div>
                  <div className="h-6 relative bg-black/60 rounded-full border border-[#8B4513] shadow-[inset_0_2px_10px_rgba(0,0,0,0.8),0_0_15px_rgba(6,182,212,0.2)] overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-900 via-cyan-500 to-blue-400 relative transition-all duration-1000 ease-out shadow-[0_0_20px_rgba(34,211,238,0.6)]"
                      style={{ width: `${arcaneProgress}%` }}
                    >
                      {/* Essence Particles Effect */}
                      <div className="absolute inset-0 overflow-hidden">
                        {[...Array(6)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                            style={{
                              left: `${Math.random() * 100}%`,
                              top: `${Math.random() * 100}%`,
                              opacity: Math.random() * 0.7,
                              animationDelay: `${Math.random() * 2}s`,
                              animationDuration: `${2 + Math.random() * 2}s`
                            }}
                          ></div>
                        ))}
                      </div>

                      <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.1)_50%,rgba(255,255,255,0.1)_75%,transparent_75%,transparent)] bg-[length:20px_20px] animate-[marching-ants_1s_linear_infinite] opacity-20"></div>
                      <div className="absolute right-0 top-0 bottom-0 w-3 bg-cyan-200/40 blur-[4px]"></div>
                    </div>
                  </div>
                  <div className="text-center mt-2 text-xs text-indigo-300 font-serif italic opacity-70">
                    {t('ui.fameLevelHint', language)}
                  </div>
                </div>
              </div>

              {/* Action Buttons - Stacked Column */}
              {!viewOnly && (
                <div className="flex flex-col gap-4 min-w-[200px]">
                  {!editing ? (
                    <>
                      <button
                        className="px-6 py-3 transition-all transform hover:scale-105 hover:brightness-110 shadow-lg flex items-center justify-center gap-2 group"
                        style={{
                          borderStyle: 'solid',
                          borderWidth: '10px',
                          borderImageSource: `url(${btnWood})`,
                          borderImageSlice: '30 fill',
                          textShadow: '1px 1px 2px black'
                        }}
                        onClick={() => setShowHeroes(true)}
                      >
                        {t('ui.heroesCollection', language)}
                      </button>

                      <button
                        className="px-6 py-3 transition-all transform hover:scale-105 hover:brightness-110 shadow-lg flex items-center justify-center gap-2 group"
                        style={{
                          borderStyle: 'solid',
                          borderWidth: '10px',
                          borderImageSource: `url(${btnWood})`,
                          borderImageSlice: '30 fill',
                          textShadow: '1px 1px 2px black'
                        }}
                        onClick={() => setShowHistory(true)}
                      >
                        {t('ui.totalMatches', language)}
                      </button>

                      <button
                        className="px-6 py-3 transition-all transform hover:scale-105 hover:brightness-110 shadow-lg flex items-center justify-center gap-2 group"
                        style={{
                          borderStyle: 'solid',
                          borderWidth: '10px',
                          borderImageSource: `url(${btnWood})`,
                          borderImageSlice: '30 fill',
                          textShadow: '1px 1px 2px black'
                        }}
                        onClick={() => setShowStatistics(true)}
                      >
                        {t('ui.statistics', language)}
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <button className="px-6 py-3 rounded-lg bg-green-900/80 hover:bg-green-800 border border-green-500/50 text-green-100 font-['Cinzel'] font-bold shadow-lg" onClick={saveProfile}>{t('ui.save', language)}</button>
                      <button className="px-6 py-3 rounded-lg bg-red-900/80 hover:bg-red-800 border border-red-500/50 text-red-100 font-['Cinzel'] font-bold shadow-lg" onClick={() => setEditing(false)}>{t('ui.cancel', language)}</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Achievement Track Section */}
          <div className="mb-12">
            {!viewOnly && (
              <AchievementTrack
                currentLevel={arcaneLevel}
                famePoints={famePoints}
                claimedRewards={claimedRewards}
                onClaimReward={handleClaimReward}
              />
            )}
          </div>

          {/* Spacer to push content up if needed, since grid is removed */}
          <div className="h-20"></div>

        </div>
      </div>

      {/* History Modal - Parchment Style */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-3xl max-h-[85vh] shadow-[0_0_50px_rgba(0,0,0,0.8)] rounded-xl overflow-hidden flex flex-col"
            style={{ backgroundImage: `url(${parchmentTexture})`, backgroundSize: 'cover' }}>

            <div className="p-6 border-b-2 border-[#5C4033]/20 flex items-center justify-between bg-[#5C4033]/10">
              <h2 className="text-4xl font-['Cinzel'] font-bold text-[#3E2723] drop-shadow-sm flex items-center gap-3">
                <span>📜</span> {t('ui.matchHistory', language)}
              </h2>
              <button className="w-10 h-10 rounded-full bg-[#3E2723] text-[#F5DEB3] hover:bg-[#5D4037] flex items-center justify-center font-bold text-xl border-2 border-[#DAA520]" onClick={() => setShowHistory(false)}>✕</button>
            </div>

            <div className="overflow-y-auto p-8 custom-scrollbar">
              <div className="space-y-4">
                {battleHistory.slice().reverse().map((g) => {
                  /* Same history list rendering */
                  const isWin = g.winnerId === (me?.id || 1)
                  const isDraw = g.winnerId === 0
                  return (
                    <div key={`${g.date}_${g.id}`} className="flex items-center justify-between p-4 rounded-lg bg-[#F5DEB3]/50 border border-[#8B4513]/20 hover:bg-[#F5DEB3]/80 transition-colors shadow-sm">
                      <div className="flex items-center gap-6">
                        <div className={`w-1.5 h-16 rounded-full ${isWin ? 'bg-green-700' : isDraw ? 'bg-amber-600' : 'bg-red-800'}`}></div>
                        <div>
                          <div className={`font-['Cinzel'] font-bold text-2xl ${isWin ? 'text-green-900' : isDraw ? 'text-amber-900' : 'text-red-900'}`}>
                            {isWin ? t('ui.victory', language) : isDraw ? t('ui.draw', language) : t('ui.defeat', language)}
                          </div>
                          <div className="text-sm text-[#5D4037] font-serif italic">{g.date} • {t('term.match', language)} #{g.id}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-[#8B4513] uppercase tracking-widest font-bold opacity-60">VS</div>
                        <div className="font-['Cinzel'] font-bold text-[#3E2723] text-xl">{(me?.id || 1) === 1 ? (g.player2Name || 'Opponent') : (g.player1Name || 'Opponent')}</div>
                      </div>
                    </div>
                  )
                })}
                {battleHistory.length === 0 && (
                  <div className="text-center py-20 text-[#5D4037]/60 italic font-serif text-xl border-2 border-dashed border-[#5D4037]/20 rounded-xl">
                    {t('ui.noMatchesYet', language)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Modal - Parchment Style */}
      {showStatistics && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] shadow-[0_0_50px_rgba(0,0,0,0.8)] rounded-xl overflow-hidden flex flex-col animate-[fadeIn_0.3s_ease-out]"
            style={{ backgroundImage: `url(${parchmentTexture})`, backgroundSize: 'cover' }}>

            <div className="p-6 border-b-2 border-[#5C4033]/20 flex items-center justify-between bg-[#5C4033]/10">
              <h2 className="text-4xl font-['Cinzel'] font-bold text-[#3E2723] drop-shadow-sm flex items-center gap-3">
                <span className="text-red-800">📊</span> {t('ui.statistics', language)}
              </h2>
              <button className="w-10 h-10 rounded-full bg-[#3E2723] text-[#F5DEB3] hover:bg-[#5D4037] flex items-center justify-center font-bold text-xl border-2 border-[#DAA520]" onClick={() => setShowStatistics(false)}>✕</button>
            </div>

            <div className="overflow-y-auto p-10 space-y-12 custom-scrollbar text-[#3E2723]">
              {/* 0. Account Overview */}
              <section>
                <h3 className="text-2xl font-['Cinzel'] font-bold border-b border-[#8B4513]/30 pb-2 mb-4">🏛️ {t('ui.lifetimeAverage', language)}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="bg-[#F5DEB3]/50 p-6 rounded-lg border-2 border-[#8B4513]/20 flex flex-col items-center">
                    <span className="text-xs uppercase tracking-widest font-black text-[#8B4513]/60 mb-2">{t('ui.totalMatches', language)}</span>
                    <span className="text-4xl font-['Cinzel'] font-bold">{battleHistory.length}</span>
                  </div>
                  <div className="bg-[#F5DEB3]/50 p-6 rounded-lg border-2 border-[#8B4513]/20 flex flex-col items-center">
                    <span className="text-xs uppercase tracking-widest font-black text-[#8B4513]/60 mb-2">{t('ui.winRate', language)}</span>
                    <span className={`text-4xl font-['Cinzel'] font-bold ${winRate >= 50 ? 'text-green-700' : 'text-red-800'}`}>{winRate}%</span>
                  </div>
                </div>
              </section>

              {/* 1. Hero Stats */}
              <section>
                <h3 className="text-2xl font-['Cinzel'] font-bold border-b border-[#8B4513]/30 pb-2 mb-4">🦸 {t('ui.stats.heroStats', language)}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b-2 border-[#8B4513]/20">
                        <th className="py-2 px-4 uppercase text-xs tracking-widest">{t('ui.heroes', language)}</th>
                        <th className="py-2 px-4 uppercase text-xs tracking-widest">{t('ui.stats.winRate', language)}</th>
                        <th className="py-2 px-4 uppercase text-xs tracking-widest">{t('ui.stats.bestClass', language)}</th>
                        <th className="py-2 px-4 uppercase text-xs tracking-widest">{t('ui.stats.worstClass', language)}</th>
                        <th className="py-2 px-4 uppercase text-xs tracking-widest">{t('ui.stats.games', language)}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#8B4513]/10">
                      {Object.keys(playerStats.heroStats).length > 0 ? (
                        Object.entries(playerStats.heroStats).map(([hero, s]) => (
                          <tr key={hero} className="bg-[#F5DEB3]/40 border-b border-[#8B4513]/10 hover:bg-[#F5DEB3]/70 transition-colors">
                            <td className="py-3 px-4 font-bold">{t(`hero.${hero.toUpperCase().replace(/[\s-]/g, '_')}.name` as any, language)}</td>
                            <td className={`py-3 px-4 font-bold ${s.winRate >= 50 ? 'text-green-700' : 'text-red-800'}`}>{s.winRate}%</td>
                            <td className="py-3 px-4 text-sm italic">
                              {(() => {
                                const bestClass = getBestClass(s.withClass);
                                return t(`class.${bestClass.toUpperCase()}.name` as any, language);
                              })()}
                            </td>
                            <td className="py-3 px-4 text-sm italic">
                              {(() => {
                                const worstClass = getWorstClass(s.withClass);
                                return t(`class.${worstClass.toUpperCase()}.name` as any, language);
                              })()}
                            </td>
                            <td className="py-3 px-4 font-mono">{s.games}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={5} className="py-8 text-center italic opacity-60">{t('ui.stats.noData', language)}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* 2. Matchup Heatmap */}
              <section>
                <h3 className="text-2xl font-['Cinzel'] font-bold border-b border-[#8B4513]/30 pb-2 mb-4">⚔️ {t('ui.stats.matchupHeatmap', language)}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.keys(playerStats.heroMatchups).length > 0 ? (
                    Object.entries(playerStats.heroMatchups)
                      .sort((a, b) => b[1].total - a[1].total)
                      .slice(0, 10)
                      .map(([key, m]) => (
                        <div key={key} className="flex justify-between items-center p-3 bg-[#F5DEB3]/50 rounded border border-[#8B4513]/20 hover:bg-[#F5DEB3]/80 transition-colors shadow-sm">
                          <span className="font-serif font-bold text-sm tracking-tight">{key}</span>
                          <div className="flex items-center gap-3">
                            <span className={`font-mono font-bold ${m.winRate >= 50 ? 'text-green-700' : 'text-red-800'}`}>{m.winRate}%</span>
                            <span className="text-xs opacity-50">({m.total} {t('ui.stats.games', language).toLowerCase()})</span>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="col-span-full py-8 text-center italic opacity-60">{t('ui.stats.noData', language)}</div>
                  )}
                </div>
              </section>

              {/* 3. Class Effectiveness */}
              <section>
                <h3 className="text-2xl font-['Cinzel'] font-bold border-b border-[#8B4513]/30 pb-2 mb-4">🛡️ {t('ui.stats.classEffectiveness', language)}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.keys(playerStats.classMatchups).length > 0 ? (
                    Object.entries(playerStats.classMatchups)
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([key, m]) => (
                        <div key={key} className="p-4 bg-[#F5DEB3]/50 rounded-lg border border-[#8B4513]/20 text-center shadow-sm hover:bg-[#F5DEB3]/80 transition-colors">
                          <div className="text-xs uppercase font-bold text-[#8B4513]/60 mb-1">{key}</div>
                          <div className={`text-2xl font-['Cinzel'] font-black ${m.winRate >= 50 ? 'text-green-700' : 'text-red-800'}`}>{m.winRate}%</div>
                          <div className="text-[10px] opacity-40 font-mono mt-1">{m.total} {t('ui.stats.games', language).toUpperCase()}</div>
                        </div>
                      ))
                  ) : (
                    <div className="col-span-full py-8 text-center italic opacity-60">{t('ui.stats.noData', language)}</div>
                  )}
                </div>
              </section>

              {/* 4. Hero vs Class */}
              <section>
                <h3 className="text-2xl font-['Cinzel'] font-bold border-b border-[#8B4513]/30 pb-2 mb-4">🎯 {t('ui.stats.heroVsClass', language)}</h3>
                <div className="flex flex-wrap gap-3">
                  {Object.keys(playerStats.heroVsClassMatchups).length > 0 ? (
                    Object.entries(playerStats.heroVsClassMatchups)
                      .filter(([_, m]) => m.total >= 1)
                      .sort((a, b) => a[1].winRate - b[1].winRate)
                      .map(([key, m]) => (
                        <div key={key} className={`px-4 py-2 rounded-full border shadow-sm font-bold flex items-center gap-2 ${m.winRate < 40 ? 'bg-red-900/10 border-red-800/40 text-red-900' : m.winRate > 60 ? 'bg-green-900/10 border-green-800/40 text-green-900' : 'bg-[#F5DEB3]/60 border-[#8B4513]/30 text-[#3E2723]'}`}>
                          <span className="opacity-60">{m.winRate < 40 ? '🔴' : m.winRate > 60 ? '🟢' : '⚪'}</span>
                          {key}: {m.winRate}%
                        </div>
                      ))
                  ) : (
                    <div className="w-full py-8 text-center italic opacity-60">{t('ui.stats.noData', language)}</div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Collections Modal (Heroes & Emotes) */}
      {showHeroes && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md flex items-center justify-center z-50 p-6">
          <div className="bg-[#1a1110] rounded-2xl border-4 border-[#5C4033] w-full max-w-6xl max-h-[90vh] shadow-2xl flex flex-col relative overflow-hidden">
            {/* Decorative Corners */}
            <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-[#DAA520] rounded-tl-xl pointer-events-none z-20"></div>
            <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-[#DAA520] rounded-tr-xl pointer-events-none z-20"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-[#DAA520] rounded-bl-xl pointer-events-none z-20"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-[#DAA520] rounded-br-xl pointer-events-none z-20"></div>

            <div className="p-8 border-b-2 border-[#5C4033] flex items-center justify-between bg-black/40"
              style={{ backgroundImage: `url(${woodPanel})`, backgroundSize: 'cover' }}>
              <div className="flex items-center gap-4">
                <h2 className="text-4xl font-['Cinzel'] font-bold text-[#FFD700] drop-shadow-md">
                  {collectionTab === 'heroes' ? t('ui.tab.heroes', language) : t('ui.tab.emotes', language)}
                </h2>
                <span className="text-lg font-bold text-[#F5DEB3] bg-black/60 px-4 py-1.5 rounded-full border border-[#DAA520]/50 ml-4 font-sans">
                  {collectionTab === 'heroes' ? `${displayCaptured.length} ${t('ui.collected', language)}` : `${Object.keys(EMOTES).length} ${t('ui.collected', language)}`}
                </span>
              </div>

              <div className="flex items-center gap-4">
                {/* Toggle Button - Closer to close button, larger */}
                <button
                  className="px-8 py-3 rounded-lg border-2 border-[#DAA520] bg-[#3E2723] text-[#FDB931] hover:bg-[#5D4037] text-lg font-['Cinzel'] font-bold transition-all shadow-xl hover:scale-105 active:scale-95 flex items-center gap-2"
                  onClick={() => setCollectionTab(prev => prev === 'heroes' ? 'emotes' : 'heroes')}
                >
                  <span className="text-2xl">{collectionTab === 'heroes' ? '🎭' : '🏛️'}</span>
                  {collectionTab === 'heroes' ? t('ui.tab.emotes', language) : t('ui.tab.heroes', language)}
                </button>
                <button className="w-12 h-12 rounded-lg bg-[#3E2723] text-[#F5DEB3] hover:bg-[#5D4037] flex items-center justify-center transition-all border-2 border-[#DAA520] shadow-lg hover:scale-110" onClick={() => setShowHeroes(false)}>✕</button>
              </div>
            </div>

            <div className="overflow-y-auto p-10 bg-[url('https://www.transparenttextures.com/patterns/dark-matter.png')] bg-stone-900">
              {collectionTab === 'heroes' ? (
                // HEROES GRID
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-6">
                  {displayCaptured.map((id: number) => {
                    /* Hero list rendering */
                    let h = heros.find(x => x.id === id)
                    if (!h) {
                      if (id === 9999) h = { id: 9999, name: 'Ferrim' }
                      else if (id === 8888) h = { id: 8888, name: 'Norim' }
                      else if (id === 7777) h = { id: 7777, name: 'Evelion' }
                      else if (id === 6666) h = { id: 6666, name: 'Coril' }
                      else if (id === 5555) h = { id: 5555, name: 'Terrik' }
                      else h = { id, name: 'Unknown' }
                    }
                    let imgSrc = h.imageUrl
                    let name = h.name
                    if (name === 'Erok-Buru') name = 'Verg'
                    if (name === 'Ferrim') imgSrc = new URL('../assets/custom/Ferrim.png', import.meta.url).href
                    else if (name === 'Norim') imgSrc = new URL('../assets/custom/Norim.png', import.meta.url).href
                    else if (name === 'Evelion') imgSrc = new URL('../assets/custom/Evelion.png', import.meta.url).href
                    else if (name === 'Coril') imgSrc = new URL('../assets/custom/Coril.png', import.meta.url).href
                    else if (name === 'Terrik') imgSrc = new URL('../assets/custom/Terrik.png', import.meta.url).href
                    else if (name === 'Gemata') imgSrc = new URL('../assets/custom/Gemata.png', import.meta.url).href
                    else if (name === 'Astrid') imgSrc = new URL('../assets/custom/Astrid.png', import.meta.url).href
                    else if (name === 'Erok-Buru') imgSrc = new URL('../assets/custom/Erok-Buru.png', import.meta.url).href
                    else if (name === 'Verg') imgSrc = new URL('../assets/custom/Verg.png', import.meta.url).href
                    else if (name === 'Lorian') imgSrc = new URL('../assets/custom/Lorian.png', import.meta.url).href
                    else if (name === 'Nyrix') imgSrc = new URL('../assets/custom/Nyrix.png', import.meta.url).href
                    else if (name === 'Shanu') imgSrc = new URL('../assets/custom/Shanu.png', import.meta.url).href
                    else if (name === 'Verisk') imgSrc = new URL('../assets/custom/Verisk.png', import.meta.url).href
                    else if (name === 'Trifle') imgSrc = new URL('../assets/custom/Trifle.png', import.meta.url).href

                    return (
                      <TouchAwareHeroItem key={id} name={name} imgSrc={imgSrc || ''} heroId={id}>
                        <div className="w-full h-full relative group-hover:scale-105 transition-transform duration-500">
                          <div className="absolute inset-0 border-[6px] border-[#2F1F1A] rounded-lg z-20 pointer-events-none group-hover:border-[#DAA520] transition-colors"></div>
                          {imgSrc ? (
                            <img src={imgSrc} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-[#1a1110] flex items-center justify-center text-[#5D4037] text-4xl">?</div>
                          )}
                          <div className="absolute bottom-2 left-1 right-1 bg-black/80 border border-[#DAA520]/50 rounded px-1 py-1 text-center z-30">
                            <div className="text-xs font-['Cinzel'] font-bold text-[#F5DEB3] truncate tracking-wide">{t(`hero.${(h as any).id === 9999 ? 'FERRIM' : (h as any).id === 8888 ? 'NORIM' : (h as any).id === 7777 ? 'EVELION' : (h as any).id === 6666 ? 'CORIL' : (h as any).id === 5555 ? 'TERRIK' : h.name.toUpperCase().replace(/[\s-]/g, '_')}.name` as any, language)}</div>
                          </div>
                        </div>
                      </TouchAwareHeroItem>
                    )
                  })}
                  {displayCaptured.length === 0 && (
                    <div className="col-span-full flex flex-col items-center justify-center py-20 text-[#5D4037]">
                      <div className="text-6xl mb-4 opacity-50">🏰</div>
                      <p className="font-['Cinzel'] text-2xl mb-2">{t('ui.noHeroesYet', language)}</p>
                      <p className="text-[#8B4513] italic">{t('ui.winToCapture', language)}</p>
                    </div>
                  )}
                </div>
              ) : (
                // EQUIPPED EMOTES SLOTS
                <div className="flex flex-wrap justify-center gap-8 mb-12 min-h-[200px] bg-black/20 p-8 rounded-3xl border-2 border-[#DAA520]/20">
                  {emoteSlots.map((slot, index) => (
                    <div
                      key={index}
                      className="group flex flex-col items-center gap-2"
                      onClick={() => !viewOnly && setShowGalleryIndex(index)}
                    >
                      <div className={`relative w-28 h-28 
                        ${!viewOnly ? 'cursor-pointer hover:scale-110 active:scale-95' : ''} 
                        transition-all duration-300 flex items-center justify-center overflow-visible`}
                        onMouseEnter={slot.type === 'animated' ? (e) => handleEmotePreview(slot.id, e.currentTarget.getBoundingClientRect()) : undefined}
                        onTouchStart={slot.type === 'animated' ? (e) => handleEmotePreview(slot.id, e.currentTarget.getBoundingClientRect()) : undefined}
                      >
                        <div className="absolute inset-0 bg-[#DAA520] opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-xl"></div>

                        {slot.type === 'animated' ? (
                          <div
                            className="w-full h-full"
                            style={{
                              backgroundImage: `url(/assets/emotes/animated/${slot.id.toLowerCase()}.png)`,
                              backgroundSize: `${(EMOTE_FRAMES[slot.id] || 8) * 100}% 100%`,
                              backgroundPosition: 'left center',
                              transform: 'translateX(-20px) scale(1.4)',
                              imageRendering: 'pixelated'
                            }}
                          />
                        ) : (
                          <img
                            src={`/assets/emotes/${slot.id.toLowerCase()}.png`}
                            alt={slot.id}
                            className="w-full h-full object-cover select-none"
                          />
                        )}
                      </div>
                      <div className="text-[#8B4513] text-xs font-['Cinzel'] opacity-60">{t('ui.slot', language)} {index + 1}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Emote Gallery Modal */}
      {showGalleryIndex !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setShowGalleryIndex(null)}></div>
          <div className="relative w-full max-w-4xl bg-[#1a1110] border-2 border-[#DAA520] rounded-2xl shadow-[0_0_100px_rgba(0,0,0,1)] overflow-hidden flex flex-col max-h-[90vh]">
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `url(${woodPanel})` }}></div>

            <div className="p-6 border-b border-[#DAA520]/30 flex justify-between items-center relative z-10">
              <h2 className="text-3xl font-['Cinzel'] font-bold text-[#FFD700]">{t('ui.selectEmote', language)} {showGalleryIndex + 1}</h2>
              <button
                onClick={() => setShowGalleryIndex(null)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#2F1F1A] border border-[#DAA520]/50 text-[#DAA520] hover:bg-[#5D4037] transition-colors"
              >✕</button>
            </div>

            <div className="p-8 overflow-y-auto relative z-10 custom-scrollbar flex-1">
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-4">
                {Object.values(EMOTES).flatMap((base) => {
                  const unlockReward = ACHIEVEMENT_REWARDS.find(r => r.emoteId === base.id);
                  const isUnlocked = !unlockReward || arcaneLevel >= unlockReward.level;
                  const reqLevel = unlockReward?.level || 1;

                  return [
                    // Premium Option (Always available or shared basic)
                    <button
                      key={`${base.id}_premium`}
                      onClick={() => handleSelectEmote(base.id, 'premium')}
                      className="group relative aspect-square rounded-full bg-[#2F1F1A] border-2 border-[#DAA520]/30 hover:border-[#DAA520] hover:scale-105 transition-all shadow-lg overflow-hidden flex items-center justify-center p-1 scale-90"
                      title={`${base.id} (Premium)`}
                    >
                      <img src={`/assets/emotes/${base.id.toLowerCase()}.png`} className="w-full h-full object-cover rounded-full" />
                      <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </button>,

                    // Animated Option - Locked based on level
                    <button
                      key={`${base.id}_animated`}
                      onClick={() => isUnlocked && handleSelectEmote(base.id, 'animated')}
                      onMouseEnter={(e) => isUnlocked && handleEmotePreview(base.id, e.currentTarget.getBoundingClientRect())}
                      onTouchStart={(e) => isUnlocked && handleEmotePreview(base.id, e.currentTarget.getBoundingClientRect())}
                      className={`group relative aspect-square transition-all flex items-center justify-center overflow-visible ${isUnlocked ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed filter grayscale -translate-y-1'}`}
                      title={isUnlocked ? `${base.id} (Animated)` : `${t('ui.locked', language)} - Lvl ${reqLevel}`}
                    >
                      <div
                        className="w-full h-full"
                        style={{
                          backgroundImage: `url(/assets/emotes/animated/${base.id.toLowerCase()}.png)`,
                          backgroundSize: `${(EMOTE_FRAMES[base.id] || 8) * 100}% 100%`,
                          backgroundPosition: 'left center',
                          transform: 'translateX(-20px) scale(1.4)',
                          imageRendering: 'pixelated',
                          opacity: isUnlocked ? 1 : 0.4
                        }}
                      />
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                          <div className="bg-black/60 backdrop-blur-[1px] p-2 rounded-full border border-red-500/50 shadow-lg">
                            <span className="text-xl">🔒</span>
                          </div>
                          <div className="absolute -bottom-1 whitespace-nowrap bg-red-900/80 border border-red-500 px-2 rounded text-[10px] font-bold text-red-100 uppercase tracking-tighter">
                            {t('ui.lvl', language)} {reqLevel}+
                          </div>
                        </div>
                      )}
                    </button>
                  ];
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Hover Tooltip - Refined */}
      {hoveredHeroProfile && (
        <div
          className={`fixed inset-0 z-[60] flex items-center justify-center animate-[fadeIn_0.2s_ease-out]`}
          onClick={() => setHoveredHeroProfile(null)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div className="bg-[#1a1110] rounded-xl border-2 border-[#DAA520] p-1 shadow-[0_0_50px_rgba(0,0,0,0.9)] max-w-lg mx-4 pointer-events-auto relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${woodPanel})` }}></div>
            <div className="relative flex gap-0">
              {hoveredHeroProfile.imgSrc && (
                <div className="w-48 h-64 shrink-0 relative">
                  <img src={hoveredHeroProfile.imgSrc} className="w-full h-full object-cover rounded-l-lg" alt={hoveredHeroProfile.name} />
                  <div className="absolute inset-0 shadow-[inset_-10px_0_20px_rgba(0,0,0,0.8)]"></div>
                </div>
              )}
              <div className="p-6 flex flex-col justify-center">
                <h3 className="text-3xl font-['Cinzel'] font-bold text-[#FFD700] mb-3 border-b border-[#DAA520]/30 pb-2">
                  {t(`hero.${hoveredHeroProfile.name.toUpperCase().replace(/[\s-]/g, '_')}.name` as any, language)}
                </h3>
                <p className="text-[#E0C097] text-lg leading-relaxed font-serif italic text-shadow-sm">{getHeroDescriptionByName(hoveredHeroProfile.name, language)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div >
  )
}

export default ProfileScreen
